import type {
  SchemaActivityEvent,
  SchemaColumn,
  SchemaDetail,
  SchemaRule,
  SchemaSummary,
} from "@/types/schemaBuilder";

const STORAGE_KEY = "schema-builder-custom-schemas";
const SESSION_KEY_PREFIX = "schema-builder-session-";
const ACTIVITY_KEY_PREFIX = "schema-builder-activity-";

// ---------------------------------------------------------------------------
// In-memory schemas cache — persists for the lifetime of the browser tab so
// the schema list page only hits the network once per session.
// ---------------------------------------------------------------------------
let _schemasCache: SchemaSummary[] | null = null;

export function getSchemasCache(): SchemaSummary[] | null {
  return _schemasCache;
}

export function setSchemasCache(schemas: SchemaSummary[]): void {
  _schemasCache = [...schemas];
}

export function upsertSchemaInCache(schema: SchemaSummary): void {
  if (!_schemasCache) {
    _schemasCache = [schema];
    return;
  }
  const idx = _schemasCache.findIndex((s) => s.id === schema.id);
  _schemasCache =
    idx >= 0
      ? _schemasCache.map((s) => (s.id === schema.id ? schema : s))
      : [schema, ..._schemasCache];
}

export function invalidateSchemasCache(): void {
  _schemasCache = null;
}

export interface ReferenceListEntry {
  id: string;
  name: string;
  usedInColumnIds: string[];
  itemCount: number;
  values: string[];
}

export interface SchemaEditSession {
  columns: SchemaColumn[];
  localActivity: SchemaActivityEvent[];
  referenceListValuesByName?: Record<string, string[]>;
  referenceLists?: ReferenceListEntry[];
  rules?: SchemaRule[];
  name?: string;
  description?: string;
}

export function getCustomSchemas(): SchemaDetail[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as SchemaDetail[];
  } catch {
    return [];
  }
}

export function saveCustomSchema(schema: SchemaDetail) {
  const existing = getCustomSchemas();
  const deduped = existing.filter((s) => s.id !== schema.id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([schema, ...deduped]));
}

export function deleteCustomSchema(schemaId: string) {
  const existing = getCustomSchemas();
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(existing.filter((schema) => schema.id !== schemaId))
  );
}

export function saveEditSession(sessionId: string, session: SchemaEditSession): void {
  localStorage.setItem(`${SESSION_KEY_PREFIX}${sessionId}`, JSON.stringify(session));
}

export function getEditSession(sessionId: string): SchemaEditSession | null {
  const raw = localStorage.getItem(`${SESSION_KEY_PREFIX}${sessionId}`);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SchemaEditSession;
  } catch {
    return null;
  }
}

export function clearEditSession(sessionId: string): void {
  localStorage.removeItem(`${SESSION_KEY_PREFIX}${sessionId}`);
}

export function saveSchemaActivityLog(schemaId: string, events: SchemaActivityEvent[]): void {
  localStorage.setItem(`${ACTIVITY_KEY_PREFIX}${schemaId}`, JSON.stringify(events));
}

export function getSchemaActivityLog(schemaId: string): SchemaActivityEvent[] {
  const raw = localStorage.getItem(`${ACTIVITY_KEY_PREFIX}${schemaId}`);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as SchemaActivityEvent[];
  } catch {
    return [];
  }
}

export function clearSchemaActivityLog(schemaId: string): void {
  localStorage.removeItem(`${ACTIVITY_KEY_PREFIX}${schemaId}`);
}
